/**
 * RBAC engine — pyramid 3-tier.
 *
 * Quy tắc đơn giản: tra bảng `ROLE_ACTIONS[role]` → có action không.
 * Đối với context-sensitive checks (vd: team_lead chỉ sửa member trong team),
 * dùng `canOn(role, action, ctx)` để truyền thêm same_team / is_owner.
 */

import type { Role, Action, AdminUser } from "@/types/admin-rbac";
import { ROLE_RANK } from "@/types/admin-rbac";

// ── Static role → actions matrix ──

const NAV_ALL = [
  "nav.users",
  "nav.earlyAccess",
  "nav.community",
  "nav.moderation",
  "nav.advisor",
  "nav.announcements",
  "nav.revenue",
  "nav.pricing",
  "nav.plans",
  "nav.subscriptions",
  "nav.trading",
  "nav.pool",
  "nav.datasources",
  "nav.registry",
  "nav.apiOps",
  "nav.ai",
  "nav.monitor",
  "nav.system",
  "nav.featureFlags",
  "nav.integrations",
] as const satisfies readonly Action[];

const NAV_OPS: Action[] = [
  "nav.users",
  "nav.earlyAccess",
  "nav.community",
  "nav.moderation",
  "nav.advisor",
  "nav.announcements",
  "nav.subscriptions",
  "nav.trading",
  "nav.datasources",
  "nav.registry",
  "nav.apiOps",
  "nav.ai",
  "nav.monitor",
  "nav.system",
  "nav.featureFlags",
];

const NAV_FINANCE: Action[] = [
  "nav.announcements",
  "nav.revenue",
  "nav.pricing",
  "nav.plans",
  "nav.subscriptions",
  "nav.trading",
  "nav.datasources",
  "nav.monitor",
];

const NAV_MODERATOR: Action[] = [
  "nav.users",
  "nav.earlyAccess",
  "nav.community",
  "nav.moderation",
  "nav.advisor",
  "nav.monitor",
];

const NAV_SUPPORT_LEAD: Action[] = [
  "nav.users",
  "nav.earlyAccess",
  "nav.advisor",
  "nav.monitor",
];

const NAV_SUPPORT: Action[] = [
  "nav.users",
  "nav.earlyAccess",
  "nav.advisor",
];

const NAV_TEAM_LEAD: Action[] = [
  "nav.users",
  "nav.earlyAccess",
  "nav.advisor",
  "nav.community",
  "nav.moderation",
  "nav.revenue",
  "nav.subscriptions",
  "nav.pricing",
  "nav.plans",
];

const NAV_ADVISOR: Action[] = [
  "nav.advisor",
  "nav.users",       // read-only — list khách hàng đang phụ trách
  "nav.earlyAccess",
  "nav.community",
];

const ROLE_ACTIONS: Record<Role, Set<Action>> = {
  super_admin: new Set<Action>([
    ...NAV_ALL,
    "advisor.viewAll",
    "advisor.viewTeam",
    "advisor.assignSelf",
    "advisor.transferIntraTeam",
    "advisor.transferCrossTeam",
    "advisor.escalate",
    "advisor.reply",
    "advisor.settings",
    "advisor.agents",
    "admin.create",
    "admin.editAny",
    "admin.editTeamMember",
    "admin.deactivate",
    "team.create",
    "team.editAny",
  ]),
  ops: new Set<Action>([
    ...NAV_OPS,
    "advisor.viewAll",
    "advisor.viewTeam",
    "advisor.assignSelf",
    "advisor.transferIntraTeam",
    "advisor.escalate",
    "advisor.reply",
  ]),
  finance: new Set<Action>([
    ...NAV_FINANCE,
  ]),
  moderator: new Set<Action>([
    ...NAV_MODERATOR,
    "advisor.viewAll",
    "advisor.viewTeam",
    "advisor.assignSelf",
    "advisor.transferIntraTeam",
    "advisor.escalate",
    "advisor.reply",
  ]),
  support_lead: new Set<Action>([
    ...NAV_SUPPORT_LEAD,
    "advisor.viewAll",
    "advisor.viewTeam",
    "advisor.assignSelf",
    "advisor.transferIntraTeam",
    "advisor.transferCrossTeam",
    "advisor.escalate",
    "advisor.reply",
    "advisor.settings",
    "advisor.agents",
  ]),
  support: new Set<Action>([
    ...NAV_SUPPORT,
    // KHÔNG có advisor.viewAll: support chỉ thấy inbox (conv đã nhận) + queue
    // (conv chưa ai nhận). Oversight toàn bộ dành cho super_admin/ops/moderator/
    // support_lead. Xem loadConversations() ở app/advisor/page.tsx.
    "advisor.assignSelf",
    "advisor.escalate",
    "advisor.reply",
  ]),
  team_lead: new Set<Action>([
    ...NAV_TEAM_LEAD,
    "advisor.viewTeam",
    "advisor.assignSelf",
    "advisor.transferIntraTeam",
    "advisor.escalate",
    "advisor.reply",
    "advisor.settings",
    "advisor.agents",
    "admin.editTeamMember",
  ]),
  advisor: new Set<Action>([
    ...NAV_ADVISOR,
    "advisor.assignSelf",
    "advisor.escalate",
    "advisor.reply",
  ]),
};

// ── Public API ──

export function can(role: Role | null | undefined, action: Action): boolean {
  if (!role) return false;
  return ROLE_ACTIONS[role]?.has(action) ?? false;
}

export function cannot(role: Role | null | undefined, action: Action): boolean {
  return !can(role, action);
}

export function hasRoleAtLeast(role: Role | null | undefined, min: Role): boolean {
  if (!role) return false;

  // Backend role gates are explicit allowlists, not a single linear hierarchy.
  // Keep the legacy `team_lead` guard equivalent to `support_lead`.
  switch (min) {
    case "super_admin":
      return role === "super_admin";
    case "support_lead":
    case "team_lead":
      return role === "super_admin" || role === "support_lead" || role === "team_lead";
    case "support":
    case "advisor":
      return (
        role === "super_admin" ||
        role === "ops" ||
        role === "moderator" ||
        role === "support_lead" ||
        role === "support" ||
        role === "team_lead" ||
        role === "advisor"
      );
    case "ops":
      return role === "super_admin" || role === "ops";
    case "finance":
      return role === "super_admin" || role === "finance";
    case "moderator":
      return role === "super_admin" || role === "moderator";
    default:
      return (ROLE_RANK[role] ?? -1) >= (ROLE_RANK[min] ?? Number.POSITIVE_INFINITY);
  }
}

/**
 * Context-aware check. Dùng cho hành động liên quan tới một resource có owner/team.
 *
 * Ví dụ:
 *   canOn(currentUser.role, "admin.editTeamMember", {
 *     actor_team_id: currentUser.team_id,
 *     target_team_id: targetAdmin.team_id,
 *   })
 */
export interface RbacContext {
  actor_team_id?: string | null;
  target_team_id?: string | null;
  /** target.manager_id === actor.id */
  is_direct_report?: boolean;
}

export function canOn(
  role: Role | null | undefined,
  action: Action,
  ctx: RbacContext,
): boolean {
  if (!can(role, action)) return false;
  if (role === "super_admin") return true;

  switch (action) {
    case "admin.editTeamMember":
    case "advisor.transferIntraTeam":
    case "advisor.viewTeam":
      return (
        !!ctx.actor_team_id &&
        ctx.actor_team_id === ctx.target_team_id
      );
    default:
      return true;
  }
}

/** Filter sidebar items theo role hiện tại. */
export function visibleNavActions(role: Role | null | undefined): Set<Action> {
  if (!role) return new Set();
  const actions = ROLE_ACTIONS[role];
  if (!actions) return new Set();
  return new Set([...actions].filter((a) => a.startsWith("nav.")));
}

/** Default landing page after login / root redirect. */
export function defaultPathForRole(role: Role | null | undefined): string {
  switch (role) {
    case "super_admin":
    case "ops":
    case "finance":
      return "/dashboard";
    case "moderator":
    case "support_lead":
    case "support":
    case "team_lead":
    case "advisor":
      return "/advisor";
    default:
      return "/dashboard";
  }
}

/** Tóm tắt scope cho UI: super_admin = 'global', team_lead/advisor = team_id. */
export function scopeOf(user: AdminUser | null): "global" | string | null {
  if (!user) return null;
  if (user.role === "super_admin") return "global";
  return user.team_id;
}
